import React, { FC, useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { Chip, TextField } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

type ChipInputProps = {
    label: string;
    placeholder: string;
    options: string[];
    defaultValue: string[];
    required?: boolean;
    disabled?: boolean;
    createChipOnBlur?: boolean;
    errorMessage?: string;
    overwriteColor?: 'error' | 'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | undefined;
    overwriteIcon?: React.ReactElement;
    onValueChange: (value: string[]) => void;
    onDelete: (value: string[]) => void;
    onError?: (err: Error | null) => void;
    chipValidator?: (value: string) => boolean;
    dataTest?: string;
};

// TODO: improve it further to be more generic

const ChipInput: FC<ChipInputProps> = ({
    onValueChange,
    onError,
    options,
    defaultValue,
    label,
    onDelete,
    placeholder,
    required,
    disabled,
    createChipOnBlur,
    errorMessage,
    chipValidator,
    overwriteIcon = <ErrorIcon />,
    overwriteColor = 'error',
    dataTest,
}: ChipInputProps) => {
    const [values, setValues] = useState<string[]>([]);
    const [hasError, setHasError] = useState(false);
    const [inputValue, setInputValue] = useState<string>('');

    const validate = (values: string[]) => {
        if (chipValidator && values.length) {
            const isValid = values.every((value) => chipValidator(value));
            return onError && onError(!isValid ? new Error('Invalid Chip Value') : null);
        }
        onError && onError(null);
    };
    const handleChange = (_: unknown, value: string | (string | string[])[] | null) => {
        setValues(value as string[]);
        onValueChange(value as string[]);
        setHasError((required && !(value as string[]).length) || false);
        validate(value as string[]);
        setInputValue('');
    };
    const handleDelete = (val: string) => {
        setHasError(false);
        const strArr = [...values];
        const index = strArr.indexOf(val);
        if (index > -1) {
            strArr.splice(index, 1);
        }
        setValues(strArr);
        onDelete(strArr);
        if (required && !strArr.length) {
            setHasError(true);
            return onError && onError(new Error('Field is required'));
        }
        validate(strArr);
    };

    useEffect(() => {
        setValues(defaultValue as string[]);
    }, [defaultValue]);

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setHasError(false);
        const strArr = [...values];
        if (value && createChipOnBlur) {
            strArr.push(value);
            setValues(strArr);
            setInputValue('');
        }
        onValueChange(strArr);
        if (required && !strArr.length) {
            setHasError(true);
            return onError && onError(new Error('Field is required'));
        }
        validate(strArr);
    };

    return (
        <Autocomplete
            multiple
            sx={{ minWidth: 280 }}
            id="tags-filled"
            options={options}
            value={values}
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
            onChange={handleChange}
            disabled={disabled}
            freeSolo
            renderTags={(value) =>
                value.map((option, index) => {
                    const isValid = chipValidator ? chipValidator(option) : true;
                    return (
                        <Chip
                            size="small"
                            disabled={disabled}
                            key={index}
                            color={isValid ? 'secondary' : overwriteColor}
                            onDelete={() => handleDelete(option)}
                            label={option}
                            deleteIcon={!isValid ? overwriteIcon : undefined}
                        />
                    );
                })
            }
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={`${label}${required ? '*' : ''}`}
                    placeholder={placeholder}
                    onBlur={handleBlur}
                    error={hasError}
                    helperText={hasError ? errorMessage : undefined}
                    data-test={dataTest}
                />
            )}
        />
    );
};

export default ChipInput;
